/** @flow */
import * as React from 'react';
import ReactHighmaps from 'react-highcharts/ReactHighmaps';
import { translate } from 'react-i18next';
import { roundNumber, calculatePercent } from 'common/helpers/math';
import { displayPercent } from 'common/components/Number';
import GeolocationReach from 'statsradio/modules/dashboard/domain/GeolocationReach';
import Metrics from '../../../../../../common/themes/Metrics';
import Fonts from '../../../../../../common/themes/Fonts';
import Colors from '../../../../../../common/themes/Colors';
import { GeoMapLayoutData } from '../../../domain/GeoMapLayout';


type Props = {
  geolocalisation: GeolocationReach[];
  layout: GeoMapLayoutData;
  zoomable?: boolean;
  aspectRatio?: number;
  onMapBoundsUpdated: (extremes: any) => void;
  t: any;
}

function parseMapData(data: GeolocationReach[]): { code: string; value: number; }[] {
  const total = data.map(d => d.reach).reduce((acc, reach) => acc + reach, 0);

  return data.map((d) => {
    return {
      code: d.getMapKey(),
      value: roundNumber(calculatePercent(d.reach, total), 2),
    };
  });
}

function doInitialZoom(chart: any, layout: any, data: GeolocationReach[]) {
  if (layout.mapBounds) {
    chart.axes[0].setExtremes(layout.mapBounds.x.min, layout.mapBounds.x.max);
    chart.axes[1].setExtremes(layout.mapBounds.y.min, layout.mapBounds.y.max);
    return;
  }

  if (data.length === 0) {
    return;
  }

  const regionWithMaxReach = data.reduce((prev, current) => (prev.reach > current.reach ? prev : current));
  const mapData = chart.series[0].mapData;
  const regionToZoom = mapData.find(m => m.code === regionWithMaxReach.getMapKey()) || mapData[0];

  chart.mapZoom(layout.initialZoom || 1, regionToZoom._midX, regionToZoom._midY);
}

function afterMapRender(chart: any) {
  chart.redraw();
  chart.reflow();
}

function GeoMapByRegion({ aspectRatio, geolocalisation, layout, t, zoomable, onMapBoundsUpdated = (bounds) => { } }: Props) {
  const data = parseMapData(geolocalisation);

  return (
    <ReactHighmaps
      callback={chart => afterMapRender(chart)}
      config={{
        title: {
          text: '',
        },
        mapNavigation: {
          enabled: zoomable,
          enableMouseWheelZoom: zoomable,
        },
        tooltip: {
          backgroundColor: '#fff',
          borderWidth: 1,
          borderColor: '#ccc',
          style: {
            ...Fonts.style.main,
            fontSize: 16,
            lineHeight: 20,
            padding: Metrics.margin,
          },
        },
        chart: {
          backgroundColor: 'none',
          height: aspectRatio ? `${aspectRatio}%` : null,
          spacing: [0, 0, Metrics.largeMargin, 0],
          events: {
            load: function () {
              doInitialZoom(this, layout, geolocalisation);
            },
          },
        },
        colorAxis: {
          min: 0,
        },
        legend: {
          width: 300,
          itemWidth: '100%',
          symbolWidth: 300,
          navigation: {
            arrowSize: 14,
          },
        },
        xAxis: {
          events: {
            afterSetExtremes: function (event) {
              const extremes = this.getExtremes();

              onMapBoundsUpdated({ x: extremes });
            },
          },
        },
        yAxis: {
          events: {
            afterSetExtremes: function (event) {
              const extremes = this.getExtremes();

              onMapBoundsUpdated({ y: extremes });
            },
          },
        },
        series: [
          {
            data: data,
            mapData: layout.map,
            name: t('reach'),
            joinBy: 'code',
            tooltip: {
              headerFormat: '',
              pointFormatter: function () {
                return `${this.name}<br /><strong>${t('reach')} ${displayPercent(this.value / 100)}</strong>`;
              },
            },
            dataLabels: {
              enabled: true,
              format: '{point.name}',
            },
            states: {
              hover: {
                color: Colors.primary,
              },
            },
          },
        ],
        credits: {
          enabled: false,
        },
      }}
    />
  );
}

export default translate()(GeoMapByRegion);



// WEBPACK FOOTER //
// ./src/statsradio/modules/dashboard/components/geolocalisation/map/GeoMapByRegion.js